import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { useStableValue } from '@shared/util/hooks';
import { getController } from '@shared/util/timeout';
import { JsonStorage } from './JsonStorage';
import { parseStoredJson } from './parseStoredJson';

/**
 * useLocalStorage
 *
 * Drop-in replacement for useState with localstorage backing.
 *
 * @param key the storage key associated with the data
 * @param defaultValue fallback value to return if stored value doesn't exist
 * @param timeToLiveMs the time for the value to be valid in the storage.
 * Defaults to undefined which means the value will never expire.
 * @returns tuple of [value, setValue, removeValue]
 *
 * Usage notes:
 * - The value is always stored as JSON, which allows for storing arrays,
 *   object, booleans, strings, or numbers.
 * - The value type can be inferred from the default value, or can be specified
 *   by calling useLocalStorage<SometType>(key, defaultValue, ttl);
 * - If value is not found in storage, or is expired, the default value will be
 *   returned.
 * - Storage is read during render, so the stored value is in place on the very
 *   first render and a changed key is reflected in the same render.
 * - The defaultValue is compared by value, so passing an inline object or array
 *   is fine - it does not need to be a constant or memoized.
 * - If you want an undefined value to be returned when the value is not found
 *   or expired, the storage type must be specified as a union with undefined,
 *   and the defaultValue should be passed as undefined:
 *
 * const [value, setValue] = useLocalStorage<T | undefined>(key, undefined);
 *
 * Updating the value:
 * - the updater accepts a SetStateAction, same as useState.
 *
 * - pass a new value directly
 * - setValue(newValue);
 *
 * - pass a function which accepts the previous value and returns the new value
 * - setValue(prev => ({ ...prev, ...changes });
 */

type UseLocalStorageHook<T> = [T, Dispatch<SetStateAction<T>>, VoidFunction];

interface StorageEntry<T> {
  key: string;
  defaultValue: T;
  value: T;
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  ttl?: number,
): UseLocalStorageHook<T> {
  // Equality-guarded, so a re-broadcast of the value we already hold doesn't re-render.
  const [entry, setEntry] = useStableValue<StorageEntry<T>>(() => readEntry(key, defaultValue));

  // Use a ref to track the default, so it can stay out of the dependency arrays
  // below, allowing callers to pass an inline object or array whose identity
  // changes every render.
  const defaultValueRef = useRef<T>(defaultValue);
  useEffect(() => {
    defaultValueRef.current = defaultValue;
  }, [defaultValue]);

  // Adjusting state during render is deliberate: React's documented pattern for
  // reacting to a changed prop, and it settles before paint.
  const isStale = entry.key !== key || !isEqual(entry.defaultValue, defaultValue);
  const current = isStale ? readEntry(key, defaultValue) : entry;
  if (isStale) {
    setEntry(current);
  }
  const value = current.value;

  const setValue = useCallback(
    (nextValue: T) => setEntry(prev => ({ ...prev, value: nextValue })),
    [setEntry],
  );

  useEffect(() => {
    const { signal, abort } = getController();
    const listener = (event: StorageEvent) => {
      if (event.key === key) {
        const storedValue = parseStoredJson<T>(event.newValue);
        setValue(storedValue ?? defaultValueRef.current);
      }
    };
    window.addEventListener('storage', listener, { signal });
    return abort;
  }, [key, setValue]);

  // Use a ref so saveValue can read the current value without a state updater,
  // keeping JsonStorage.set (and its synchronous StorageEvent dispatch) out of
  // React's render phase.
  const valueRef = useRef<T>(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const saveValue = useCallback(
    (update: SetStateAction<T>) => {
      const prev = valueRef.current;
      const updatedValue = update instanceof Function ? update(prev) : update;
      if (!isEqual(prev, updatedValue)) {
        setValue(updatedValue);
        JsonStorage.set(key, updatedValue, ttl);
      }
    },
    [key, ttl, setValue],
  );

  const removeValue = useCallback(() => {
    JsonStorage.remove(key);
    setValue(defaultValueRef.current);
  }, [key, setValue]);

  return [value, saveValue, removeValue];
}

function readEntry<T>(key: string, defaultValue: T): StorageEntry<T> {
  return { key, defaultValue, value: JsonStorage.get<T>(key) ?? defaultValue };
}
